import { useState, useEffect } from 'react';
import type { Flow, Portal, PortalCampoConfig, PortalAparencia, PortalFuncionalidades, PortalNotificacoes, Colaborador } from '../../types';
import PortalSubmissoes from './PortalSubmissoes';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
  CheckIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  PhotoIcon,
  PaintBrushIcon,
  Cog6ToothIcon,
  InboxIcon
} from '@heroicons/react/24/outline';

interface PortaisProps {
  flow: Flow;
  onClose?: () => void;
  userPermissions?: {
    canEdit: boolean;
    canAdmin: boolean;
    currentUser?: any;
  };
  permissoesGranulares?: any[];
  colaboradores?: Colaborador[];
}

const API_BASE = (import.meta as any).env?.VITE_API_URL || '';

const Portais = ({ flow, userPermissions, permissoesGranulares, colaboradores = [] }: PortaisProps) => {
  const [portais, setPortais] = useState<Portal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState<Portal | null>(null);
  const [criando, setCriando] = useState(false);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [verSubmissoes, setVerSubmissoes] = useState<Portal | null>(null);

  // Carregar portais ao montar
  useEffect(() => {
    carregarPortais();
  }, [flow.id]);

  const carregarPortais = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/portais?flowId=${flow.id}&empresaId=${flow.empresaId}`);
      const data = await response.json();
      
      if (data.success) {
        setPortais(data.portais || []);
      } else {
        setError(data.error || 'Erro ao carregar portais');
      }
    } catch (err) {
      console.error('Erro ao carregar portais:', err);
      setError('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const criarPortal = async (dadosPortal: Partial<Portal>) => {
    try {
      const response = await fetch(`${API_BASE}/api/portais`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...dadosPortal,
          flowId: flow.id,
          empresaId: flow.empresaId,
          criadoPor: userPermissions?.currentUser?.id
        })
      });
      
      const data = await response.json();
      if (data.success) {
        setPortais([...portais, data.portal]);
        setCriando(false);
        return data.portal;
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Erro ao criar portal:', err);
      setError('Erro ao criar portal');
    }
  };

  const atualizarPortal = async (portalId: string, dados: Partial<Portal>) => {
    try {
      const response = await fetch(`${API_BASE}/api/portais/${portalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dados)
      });
      
      const data = await response.json();
      if (data.success) {
        setPortais(portais.map(p => p.id === portalId ? data.portal : p));
        setEditando(null);
        return data.portal;
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Erro ao atualizar portal:', err);
      setError('Erro ao atualizar portal');
    }
  };

  const deletarPortal = async (portalId: string) => {
    if (!confirm('Tem certeza que deseja excluir este portal? O link de acesso será invalidado.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/portais/${portalId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        setPortais(portais.filter(p => p.id !== portalId));
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Erro ao deletar portal:', err);
      setError('Erro ao deletar portal');
    }
  };

  const regenerarToken = async (portalId: string) => {
    if (!confirm('Regenerar o token irá invalidar o link atual. Continuar?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/portais/regenerar-token/${portalId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        setPortais(portais.map(p => 
          p.id === portalId ? { ...p, tokenPublico: data.tokenPublico } : p
        ));
      } else {
        setError(data.error);
      }
    } catch (err) {
      console.error('Erro ao regenerar token:', err);
      setError('Erro ao regenerar token');
    }
  };

  const copiarLink = async (portal: Portal) => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? window.location.origin
      : 'https://escalone.duckdns.org';
    const link = `${baseUrl}/portal/${portal.tokenPublico}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiado(portal.id);
      setTimeout(() => setCopiado(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
      prompt('Copie o link:', link);
    }
  };

  const toggleAtivo = async (portal: Portal) => {
    await atualizarPortal(portal.id, { ativo: !portal.ativo });
  };

  // Verificar permissão: admin global OU permissão granular de portal
  const userId = userPermissions?.currentUser?.id;
  const userEmail = userPermissions?.currentUser?.email?.toLowerCase();
  // Encontrar o colaborador correspondente ao usuário logado (pode ser por ID ou email)
  const meuColaborador = colaboradores.find(c => 
    c.id === userId || c.userId === userId || c.email?.toLowerCase() === userEmail
  );
  const meuColaboradorId = meuColaborador?.id || userId;
  const permGranular = permissoesGranulares?.find((p: any) => p.colaboradorId === meuColaboradorId);
  const portalVisualizar = permGranular?.permissoesGerais?.gerenciarPortal?.visualizar;
  const portalEditar = permGranular?.permissoesGerais?.gerenciarPortal?.editar;
  const temAcessoPortal = userPermissions?.canAdmin || portalVisualizar || portalEditar;
  const podeEditarPortal = userPermissions?.canAdmin || portalEditar;

  if (!temAcessoPortal) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-500">
          <GlobeAltIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg font-medium">Acesso Restrito</p>
          <p className="text-sm">Você não tem permissão para acessar os portais. Solicite ao administrador.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Carregando portais...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <GlobeAltIcon className="h-6 w-6 text-blue-600" />
              Portais de Comunicação Externa
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Configure formulários públicos para receber dados de fornecedores, clientes e parceiros externos.
            </p>
          </div>
          {podeEditarPortal && (
            <button
              onClick={() => setCriando(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Novo Portal
            </button>
          )}
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Modal de Criação/Edição */}
      {(criando || editando) && (
        <PortalEditor
          portal={editando}
          flow={flow}
          onSave={async (dados) => {
            if (editando) {
              await atualizarPortal(editando.id, dados);
            } else {
              await criarPortal(dados);
            }
          }}
          onCancel={() => {
            setCriando(false);
            setEditando(null);
          }}
        />
      )}

      {/* Lista de Portais */}
      <div className="space-y-4">
        {portais.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-dashed border-gray-300">
            <GlobeAltIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Nenhum portal configurado</p>
            <p className="text-gray-400 text-sm mt-1">
              {podeEditarPortal ? 'Crie um portal para permitir que pessoas externas enviem dados para o fluxo.' : 'Nenhum portal foi criado ainda neste fluxo.'}
            </p>
            {podeEditarPortal && (
              <button
                onClick={() => setCriando(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Criar Primeiro Portal
              </button>
            )}
          </div>
        ) : (
          portais.map((portal) => (
            <PortalCard
              key={portal.id}
              portal={portal}
              flow={flow}
              expandido={expandido === portal.id}
              copiado={copiado === portal.id}
              podeEditar={!!podeEditarPortal}
              onToggleExpand={() => setExpandido(expandido === portal.id ? null : portal.id)}
              onEdit={() => setEditando(portal)}
              onDelete={() => deletarPortal(portal.id)}
              onToggleAtivo={() => toggleAtivo(portal)}
              onCopiarLink={() => copiarLink(portal)}
              onRegenerarToken={() => regenerarToken(portal.id)}
              onVerSubmissoes={() => setVerSubmissoes(portal)}
            />
          ))
        )}
      </div>

      {/* Modal de Submissões */}
      {verSubmissoes && (
        <PortalSubmissoes
          portal={verSubmissoes}
          onClose={() => setVerSubmissoes(null)}
          currentUser={userPermissions?.currentUser}
        />
      )}
    </div>
  );
};

// Componente Card de Portal
interface PortalCardProps {
  portal: Portal;
  flow: Flow;
  expandido: boolean;
  copiado: boolean;
  podeEditar?: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleAtivo: () => void;
  onCopiarLink: () => void;
  onRegenerarToken: () => void;
  onVerSubmissoes: () => void;
}

const PortalCard = ({
  portal,
  flow,
  expandido,
  copiado,
  podeEditar = true,
  onToggleExpand,
  onEdit,
  onDelete,
  onToggleAtivo,
  onCopiarLink,
  onRegenerarToken,
  onVerSubmissoes
}: PortalCardProps) => {
  const etapaDestino = flow.etapas?.find(e => e.id === portal.etapaDestinoId);
  // Usar URL pública configurada ou origem atual (adapta-se a qualquer domínio/tunnel)
  const urlPublicaBase = portal.urlPublica || window.location.origin;
  const linkPublico = `${urlPublicaBase}/portal/${portal.tokenPublico}`;

  return (
    <div className={`bg-white rounded-lg border ${portal.ativo ? 'border-gray-200' : 'border-orange-200 bg-orange-50/30'} shadow-sm overflow-hidden`}>
      {/* Header do Card */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: portal.corPrimaria || '#3B82F6' }}
          >
            <GlobeAltIcon className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{portal.nome}</h3>
            <p className="text-sm text-gray-500 truncate">
              {portal.descricao || 'Sem descrição'} • {portal.campos?.length || 0} campos
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Botão Acessar Portal - Mais visível */}
          <a
            href={linkPublico}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            title="Abrir portal em nova aba"
          >
            <GlobeAltIcon className="h-4 w-4" />
            Acessar
          </a>

          {/* Ver Submissões */}
          <button
            onClick={onVerSubmissoes}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
            title="Ver submissões recebidas"
          >
            <InboxIcon className="h-4 w-4" />
            Submissões
            {(portal.totalSubmissoes || 0) > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-indigo-600 text-white text-xs rounded-full">
                {portal.totalSubmissoes}
              </span>
            )}
          </button>

          {/* Status Ativo/Inativo */}
          {podeEditar && (
            <button
              onClick={onToggleAtivo}
              className={`p-2 rounded-lg transition-colors ${
                portal.ativo 
                  ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                  : 'bg-orange-100 text-orange-600 hover:bg-orange-200'
              }`}
              title={portal.ativo ? 'Desativar portal' : 'Ativar portal'}
            >
              {portal.ativo ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
            </button>
          )}
          
          {/* Copiar Link */}
          <button
            onClick={onCopiarLink}
            className={`p-2 rounded-lg transition-colors ${
              copiado 
                ? 'bg-green-100 text-green-600' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Copiar link do portal"
          >
            {copiado ? <CheckIcon className="h-5 w-5" /> : <ClipboardDocumentIcon className="h-5 w-5" />}
          </button>
          
          {/* Editar */}
          {podeEditar && (
            <button
              onClick={onEdit}
              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
              title="Editar portal"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )}
          
          {/* Expandir */}
          <button
            onClick={onToggleExpand}
            className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            title="Ver detalhes"
          >
            {expandido ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Detalhes Expandidos */}
      {expandido && (
        <div className="border-t border-gray-100 p-4 bg-gray-50/50 space-y-4">
          {/* Link do Portal */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Link Público</label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={linkPublico}
                className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 font-mono"
              />
              <button
                onClick={onCopiarLink}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                {copiado ? 'Copiado!' : 'Copiar'}
              </button>
              <a
                href={linkPublico}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
              >
                Abrir
              </a>
            </div>
          </div>

          {/* Destino dos Dados */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Etapa de Destino</label>
              <p className="mt-1 text-sm text-gray-900">{etapaDestino?.nome || 'Não definida'}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total de Submissões</label>
              <div className="mt-1 flex items-center gap-2">
                <p className="text-sm text-gray-900">{portal.totalSubmissoes || 0}</p>
                <button
                  onClick={onVerSubmissoes}
                  className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-1"
                >
                  <InboxIcon className="w-3 h-3" />
                  Ver
                </button>
              </div>
            </div>
          </div>

          {/* Campos do Formulário */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campos do Formulário</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {(portal.campos || []).map((campo, idx) => (
                <span 
                  key={idx}
                  className={`px-2 py-1 text-xs rounded-full ${
                    campo.obrigatorio 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {campo.nome} {campo.obrigatorio && '*'}
                </span>
              ))}
              {(!portal.campos || portal.campos.length === 0) && (
                <span className="text-sm text-gray-400">Nenhum campo configurado</span>
              )}
            </div>
          </div>

          {/* Ações */}
          {podeEditar && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <button
                onClick={onRegenerarToken}
                className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-700"
              >
                <ArrowPathIcon className="h-4 w-4" />
                Regenerar Link
              </button>
              <button
                onClick={onDelete}
                className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4" />
                Excluir Portal
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Editor de Portal (Modal) - Versão Robusta
interface PortalEditorProps {
  portal: Portal | null;
  flow: Flow;
  onSave: (dados: Partial<Portal>) => Promise<void>;
  onCancel: () => void;
}

const PortalEditor = ({ portal, flow, onSave, onCancel }: PortalEditorProps) => {
  // Estados básicos
  const [nome, setNome] = useState(portal?.nome || '');
  const [descricao, setDescricao] = useState(portal?.descricao || '');
  const [etapaDestinoId, setEtapaDestinoId] = useState(portal?.etapaDestinoId || '');
  const [campos, setCampos] = useState<PortalCampoConfig[]>(portal?.campos || []);
  const [ativo, setAtivo] = useState(portal?.ativo ?? true);
  const [urlPublica, setUrlPublica] = useState(portal?.urlPublica || '');
  const [salvando, setSalvando] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState<'geral' | 'campos' | 'aparencia' | 'funcionalidades' | 'notificacoes'>('geral');

  // Estados de Aparência
  const [aparencia, setAparencia] = useState<PortalAparencia>(portal?.aparencia || {
    corPrimaria: '#3B82F6',
    corSecundaria: '#1E40AF',
    corFundo: '#F3F4F6',
    corTexto: '#1F2937',
    corTextoSecundario: '#6B7280',
    tipoFundo: 'cor',
    gradienteCor1: '#667eea',
    gradienteCor2: '#764ba2',
    gradienteAngulo: 135,
    logo: '',
    imagemBanner: '',
    imagemFundo: '',
    borderRadius: '12',
    sombraCards: true
  });

  // Estados de Textos
  const [textos, setTextos] = useState({
    mensagemInicial: portal?.textos?.mensagemInicial || portal?.mensagemInicial || 'Preencha o formulário abaixo',
    mensagemSucesso: portal?.textos?.mensagemSucesso || portal?.mensagemSucesso || 'Formulário enviado com sucesso!',
    botaoEnviar: portal?.textos?.botaoEnviar || 'Enviar',
    tituloPaginaAcompanhamento: portal?.textos?.tituloPaginaAcompanhamento || 'Acompanhamento da Solicitação'
  });

  // Estados de Funcionalidades
  const [funcionalidades, setFuncionalidades] = useState<PortalFuncionalidades>(portal?.funcionalidades || {
    permitirChat: true,
    permitirAcompanhamento: true,
    gerarProtocolo: true,
    exibirEtapaAtual: true,
    exibirHistorico: false,
    permitirAnexos: true,
    limitarTamanhoAnexo: 10,
    extensoesPermitidas: ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'],
    mostrarCampoTitulo: true  // Novo: permitir escolher se mostra o campo de título no formulário
  });

  // Estados de Notificações
  const [notificacoes, setNotificacoes] = useState<PortalNotificacoes>(portal?.notificacoes || {
    enviarEmailConfirmacao: true,
    enviarEmailAtualizacao: true,
    emailsInternosNotificacao: [],
    templateEmailConfirmacao: '',
    templateEmailAtualizacao: ''
  });

  const handleSalvar = async () => {
    if (!nome.trim()) {
      alert('Nome é obrigatório');
      return;
    }
    if (!etapaDestinoId) {
      alert('Selecione uma etapa de destino');
      return;
    }

    setSalvando(true);
    try {
      await onSave({
        nome: nome.trim(),
        descricao: descricao.trim(),
        etapaDestinoId,
        campos,
        aparencia,
        textos,
        funcionalidades,
        notificacoes,
        ativo,
        urlPublica: urlPublica.trim() || undefined,
        criarNovoCard: true,
        // Manter compatibilidade com versão anterior
        corPrimaria: aparencia.corPrimaria,
        mensagemInicial: textos.mensagemInicial,
        mensagemSucesso: textos.mensagemSucesso
      });
    } finally {
      setSalvando(false);
    }
  };

  const adicionarCampo = () => {
    const novoCampo: PortalCampoConfig = {
      id: `campo_${Date.now()}`,
      nome: 'Novo Campo',
      tipo: 'texto',
      obrigatorio: false,
      ordem: campos.length,
      visivelAposEnvio: true,
      editavelAposEnvio: false
    };
    setCampos([...campos, novoCampo]);
  };

  const atualizarCampo = (index: number, dados: Partial<PortalCampoConfig>) => {
    const novosCampos = [...campos];
    novosCampos[index] = { ...novosCampos[index], ...dados };
    setCampos(novosCampos);
  };

  const removerCampo = (index: number) => {
    setCampos(campos.filter((_, i) => i !== index));
  };

  const importarCamposDaEtapa = (etapaId?: string) => {
    const idEtapa = etapaId || etapaDestinoId;
    const etapa = flow.etapas?.find(e => e.id === idEtapa);
    if (!etapa?.campos?.length) {
      alert('A etapa selecionada não possui campos configurados');
      return;
    }

    const camposImportados: PortalCampoConfig[] = etapa.campos.map((campo, idx) => ({
      id: `importado_${campo.id}_${Date.now()}`,
      campoOriginalId: campo.id,
      nome: campo.nome,
      tipo: campo.tipo === 'texto_longo' ? 'texto_longo' : 
            campo.tipo === 'numero' ? 'numero' :
            campo.tipo === 'data' || campo.tipo === 'data_vencimento' ? 'data' :
            campo.tipo === 'lista' ? 'lista' :
            campo.tipo === 'checkbox_single' ? 'checkbox_single' :
            campo.tipo === 'checkbox_multi' ? 'checkbox_multi' :
            campo.tipo === 'checkbox' ? 'checkbox' :
            campo.tipo === 'justificativa_checkbox' ? 'justificativa_checkbox' :
            campo.tipo === 'valor' ? 'valor' :
            campo.tipo === 'anexo' ? 'anexo' : 'texto',
      obrigatorio: campo.obrigatorio,
      opcoes: campo.opcoes,
      ordem: idx,
      visivelAposEnvio: true,
      editavelAposEnvio: false,
      campoVinculadoId: campo.campoVinculadoId,
    }));

    if (campos.length > 0) {
      const substituir = confirm(`Você já tem ${campos.length} campo(s) configurado(s).\n\nClique OK para SUBSTITUIR pelos campos da etapa "${etapa.nome}"\nClique Cancelar para ADICIONAR aos campos existentes`);
      if (substituir) {
        setCampos(camposImportados);
      } else {
        setCampos([...campos, ...camposImportados.map((c, i) => ({ ...c, ordem: campos.length + i }))]);
      }
    } else {
      setCampos(camposImportados);
    }
  };

  const atualizarAparencia = (campo: keyof PortalAparencia, valor: any) => {
    setAparencia(prev => ({ ...prev, [campo]: valor }));
  };

  const atualizarTextos = (campo: string, valor: string) => {
    setTextos(prev => ({ ...prev, [campo]: valor }));
  };

  const atualizarFuncionalidades = (campo: keyof PortalFuncionalidades, valor: any) => {
    setFuncionalidades(prev => ({ ...prev, [campo]: valor }));
  };

  const atualizarNotificacoes = (campo: keyof PortalNotificacoes, valor: any) => {
    setNotificacoes(prev => ({ ...prev, [campo]: valor }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600">
          <h3 className="text-lg font-semibold text-white">
            {portal ? 'Editar Portal' : 'Criar Novo Portal'}
          </h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Abas */}
        <div className="px-6 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-1 overflow-x-auto">
            {([
              { id: 'geral', label: 'Geral', icon: Cog6ToothIcon },
              { id: 'campos', label: 'Campos', icon: DocumentTextIcon },
              { id: 'aparencia', label: 'Aparência', icon: PaintBrushIcon },
              { id: 'funcionalidades', label: 'Funcionalidades', icon: ChatBubbleLeftRightIcon },
              { id: 'notificacoes', label: 'Notificações', icon: BellIcon }
            ] as const).map((aba) => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id)}
                className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  abaAtiva === aba.id
                    ? 'border-blue-600 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <aba.icon className="h-4 w-4" />
                {aba.label}
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Aba Geral */}
          {abaAtiva === 'geral' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Portal *</label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Cadastro de Fornecedores"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Descrição do portal (apenas para uso interno)"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Etapa de Destino *</label>
                <select
                  value={etapaDestinoId}
                  onChange={(e) => setEtapaDestinoId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione uma etapa...</option>
                  {flow.etapas?.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>{etapa.nome}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Os dados enviados pelo portal criarão cards nesta etapa.</p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="text-sm text-gray-700">Portal ativo (link funcionando)</label>
              </div>

              {/* URL Pública para acesso externo */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                <label className="block text-sm font-medium text-blue-800 mb-1">
                  🌐 URL Pública do Portal (para usuários externos)
                </label>
                <input
                  type="url"
                  value={urlPublica}
                  onChange={(e) => setUrlPublica(e.target.value)}
                  placeholder="https://portal.suaempresa.com.br"
                  className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                />
                <p className="text-xs text-blue-600 mt-2">
                  <strong>Importante:</strong> Se você tem um domínio público (ex: portal.suaempresa.com.br) que aponta para este servidor,
                  insira aqui para que os links gerados funcionem para usuários externos à sua rede.
                  Deixe em branco para usar a URL atual do sistema.
                </p>
              </div>

              {/* Textos */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4">Textos do Portal</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Boas-vindas</label>
                    <textarea
                      value={textos.mensagemInicial}
                      onChange={(e) => atualizarTextos('mensagemInicial', e.target.value)}
                      placeholder="Texto que aparece no topo do formulário"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Sucesso</label>
                    <textarea
                      value={textos.mensagemSucesso}
                      onChange={(e) => atualizarTextos('mensagemSucesso', e.target.value)}
                      placeholder="Mensagem exibida após o envio do formulário"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Botão Enviar</label>
                      <input
                        type="text"
                        value={textos.botaoEnviar}
                        onChange={(e) => atualizarTextos('botaoEnviar', e.target.value)}
                        placeholder="Enviar"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título da Página de Acompanhamento</label>
                      <input
                        type="text"
                        value={textos.tituloPaginaAcompanhamento}
                        onChange={(e) => atualizarTextos('tituloPaginaAcompanhamento', e.target.value)}
                        placeholder="Acompanhamento"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Aba Campos */}
          {abaAtiva === 'campos' && (
            <div className="space-y-4">
              {/* Seção de Importação Rápida */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <DocumentTextIcon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900">Importar campos de uma etapa</h4>
                    <p className="text-sm text-blue-700 mt-0.5">
                      Importe todos os campos configurados em uma etapa do fluxo de uma vez só.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {flow.etapas?.filter(e => e.campos && e.campos.length > 0).map((etapa) => (
                        <button
                          key={etapa.id}
                          onClick={() => importarCamposDaEtapa(etapa.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            etapa.id === etapaDestinoId 
                              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
                              : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-100'
                          }`}
                        >
                          {etapa.nome} ({etapa.campos?.length || 0} campos)
                          {etapa.id === etapaDestinoId && ' ⭐'}
                        </button>
                      ))}
                      {(!flow.etapas || flow.etapas.filter(e => e.campos && e.campos.length > 0).length === 0) && (
                        <p className="text-sm text-blue-600 italic">Nenhuma etapa tem campos configurados</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* O portal publica apenas campos que existem na etapa de destino. Campos
                  importados de outra etapa (ou manuais) são descartados pelo servidor —
                  sem este aviso eles somem da página pública sem explicação nenhuma. */}
              {(() => {
                const etapaDestino = flow.etapas?.find(e => e.id === etapaDestinoId);
                if (!etapaDestino) return null;
                const idsDaEtapa = new Set((etapaDestino.campos || []).map(c => String(c.id)));
                const orfaos = campos.filter(c => !idsDaEtapa.has(String((c as any).campoOriginalId || c.id)));
                if (orfaos.length === 0) return null;
                return (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
                    <p className="text-sm font-semibold text-red-800">
                      ⚠️ {orfaos.length} campo(s) não vão aparecer no portal
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      O portal publica apenas campos que existem na etapa de destino
                      (<span className="font-semibold">{etapaDestino.nome}</span>). Estes vieram de outra
                      etapa ou foram criados manualmente, e são descartados no envio:
                    </p>
                    <p className="text-xs text-red-800 font-medium mt-1">
                      {orfaos.map(c => c.nome || '(sem nome)').join(' · ')}
                    </p>
                    <p className="text-xs text-red-700 mt-1.5">
                      Para resolver: importe os campos da etapa <span className="font-semibold">{etapaDestino.nome}</span> (a
                      marcada com ⭐), ou mude a etapa de destino na aba Geral.
                    </p>
                  </div>
                );
              })()}

              {/* Header da lista de campos */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 font-medium">
                  {campos.length > 0
                    ? `${campos.length} campo(s) configurado(s)`
                    : 'Nenhum campo configurado ainda'}
                </p>
                <button
                  onClick={adicionarCampo}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <PlusIcon className="h-4 w-4" />
                  Adicionar Campo Manual
                </button>
              </div>

              {campos.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">Importe campos de uma etapa acima ou adicione manualmente</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {campos.map((campo, index) => (
                    <div key={campo.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="grid grid-cols-12 gap-3 items-start">
                        <div className="col-span-4">
                          <label className="text-xs text-gray-500">Nome do Campo</label>
                          <input
                            type="text"
                            value={campo.nome}
                            onChange={(e) => atualizarCampo(index, { nome: e.target.value })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                        <div className="col-span-3">
                          <label className="text-xs text-gray-500">Tipo</label>
                          <select
                            value={campo.tipo}
                            onChange={(e) => atualizarCampo(index, { tipo: e.target.value as any })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="texto">Texto</option>
                            <option value="texto_longo">Texto Longo</option>
                            <option value="email">E-mail</option>
                            <option value="telefone">Telefone</option>
                            <option value="cpf_cnpj">CPF/CNPJ</option>
                            <option value="data">Data</option>
                            <option value="lista">Lista</option>
                            <option value="checkbox">Checkbox</option>
                            <option value="valor">Valor (R$)</option>
                            <option value="anexo">Anexo</option>
                          </select>
                        </div>
                        <div className="col-span-3">
                          <label className="text-xs text-gray-500">Vincular a Campo</label>
                          <select
                            value={campo.campoOriginalId || ''}
                            onChange={(e) => atualizarCampo(index, { campoOriginalId: e.target.value || undefined })}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Nenhum (novo campo)</option>
                            {flow.etapas?.find(e => e.id === etapaDestinoId)?.campos?.map((c) => (
                              <option key={c.id} value={c.id}>{c.nome}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-2 flex items-end gap-2">
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={campo.obrigatorio}
                              onChange={(e) => atualizarCampo(index, { obrigatorio: e.target.checked })}
                              className="w-3 h-3"
                            />
                            Obrigatório
                          </label>
                          <button
                            onClick={() => removerCampo(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Opções adicionais */}
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        {/* Opções para campos do tipo lista */}
                        {campo.tipo === 'lista' && (
                          <div className="col-span-2">
                            <label className="text-xs text-gray-500">Opções (uma por linha)</label>
                            <textarea
                              value={(campo.opcoes || []).join('\n')}
                              onChange={(e) => atualizarCampo(index, { 
                                opcoes: e.target.value.split('\n').filter(o => o.trim()) 
                              })}
                              placeholder="Opção 1&#10;Opção 2&#10;Opção 3"
                              rows={3}
                              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                        )}

                        {/* Placeholder */}
                        <div>
                          <label className="text-xs text-gray-500">Placeholder</label>
                          <input
                            type="text"
                            value={campo.placeholder || ''}
                            onChange={(e) => atualizarCampo(index, { placeholder: e.target.value })}
                            placeholder="Texto de ajuda..."
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          />
                        </div>

                        {/* Descrição */}
                        <div>
                          <label className="text-xs text-gray-500">Descrição/Ajuda</label>
                          <input
                            type="text"
                            value={campo.descricao || ''}
                            onChange={(e) => atualizarCampo(index, { descricao: e.target.value })}
                            placeholder="Texto explicativo..."
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          />
                        </div>

                        {/* Opções de visibilidade após envio */}
                        <div className="col-span-2 flex gap-4">
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={campo.visivelAposEnvio !== false}
                              onChange={(e) => atualizarCampo(index, { visivelAposEnvio: e.target.checked })}
                              className="w-3 h-3"
                            />
                            Visível na página de acompanhamento
                          </label>
                          <label className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={campo.editavelAposEnvio === true}
                              onChange={(e) => atualizarCampo(index, { editavelAposEnvio: e.target.checked })}
                              className="w-3 h-3"
                            />
                            Editável após envio
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Aba Aparência */}
          {abaAtiva === 'aparencia' && (
            <div className="space-y-6">
              {/* Cores */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <PaintBrushIcon className="h-5 w-5" />
                  Cores
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor Primária</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={aparencia.corPrimaria}
                        onChange={(e) => atualizarAparencia('corPrimaria', e.target.value)}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={aparencia.corPrimaria}
                        onChange={(e) => atualizarAparencia('corPrimaria', e.target.value)}
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor Secundária</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={aparencia.corSecundaria || '#1E40AF'}
                        onChange={(e) => atualizarAparencia('corSecundaria', e.target.value)}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={aparencia.corSecundaria || '#1E40AF'}
                        onChange={(e) => atualizarAparencia('corSecundaria', e.target.value)}
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor do Texto</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={aparencia.corTexto || '#1F2937'}
                        onChange={(e) => atualizarAparencia('corTexto', e.target.value)}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={aparencia.corTexto || '#1F2937'}
                        onChange={(e) => atualizarAparencia('corTexto', e.target.value)}
                        className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Fundo */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <PhotoIcon className="h-5 w-5" />
                  Plano de Fundo
                </h4>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Fundo</label>
                  <div className="flex gap-2">
                    {(['cor', 'gradiente', 'imagem'] as const).map((tipo) => (
                      <button
                        key={tipo}
                        onClick={() => atualizarAparencia('tipoFundo', tipo)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          aparencia.tipoFundo === tipo
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tipo === 'cor' && 'Cor Sólida'}
                        {tipo === 'gradiente' && 'Gradiente'}
                        {tipo === 'imagem' && 'Imagem'}
                      </button>
                    ))}
                  </div>
                </div>

                {aparencia.tipoFundo === 'cor' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cor de Fundo</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={aparencia.corFundo || '#F3F4F6'}
                        onChange={(e) => atualizarAparencia('corFundo', e.target.value)}
                        className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={aparencia.corFundo || '#F3F4F6'}
                        onChange={(e) => atualizarAparencia('corFundo', e.target.value)}
                        className="w-28 px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
                      />
                    </div>
                  </div>
                )}

                {aparencia.tipoFundo === 'gradiente' && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cor 1</label>
                      <input
                        type="color"
                        value={aparencia.gradienteCor1 || '#667eea'}
                        onChange={(e) => atualizarAparencia('gradienteCor1', e.target.value)}
                        className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cor 2</label>
                      <input
                        type="color"
                        value={aparencia.gradienteCor2 || '#764ba2'}
                        onChange={(e) => atualizarAparencia('gradienteCor2', e.target.value)}
                        className="w-full h-10 rounded border border-gray-300 cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ângulo ({aparencia.gradienteAngulo || 135}°)</label>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        value={aparencia.gradienteAngulo || 135}
                        onChange={(e) => atualizarAparencia('gradienteAngulo', parseInt(e.target.value))}
                        className="w-full h-10"
                      />
                    </div>
                  </div>
                )}

                {aparencia.tipoFundo === 'imagem' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL da Imagem de Fundo</label>
                    <input
                      type="url"
                      value={aparencia.imagemFundo || ''}
                      onChange={(e) => atualizarAparencia('imagemFundo', e.target.value)}
                      placeholder="https://exemplo.com/imagem.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              {/* Imagens */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4">Imagens</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL do Logo</label>
                    <input
                      type="url"
                      value={aparencia.logo || ''}
                      onChange={(e) => atualizarAparencia('logo', e.target.value)}
                      placeholder="https://exemplo.com/logo.png"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL do Banner</label>
                    <input
                      type="url"
                      value={aparencia.imagemBanner || ''}
                      onChange={(e) => atualizarAparencia('imagemBanner', e.target.value)}
                      placeholder="https://exemplo.com/banner.jpg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Estilo do Header */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                  Estilo do Header
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { value: 'solido', label: 'Sólido', desc: 'Cor única' },
                    { value: 'gradiente', label: 'Gradiente', desc: 'Primária → Secundária' },
                    { value: 'transparente', label: 'Transparente', desc: 'Com blur de fundo' },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => atualizarAparencia('estiloHeader', opt.value)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        (aparencia.estiloHeader || 'gradiente') === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Estilo dos Cards */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                  Estilo dos Cards
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {([
                    { value: 'elevado', label: 'Elevado', desc: 'Sombra suave' },
                    { value: 'glass', label: 'Glass', desc: 'Efeito vidro' },
                    { value: 'flat', label: 'Flat', desc: 'Sem sombra' },
                    { value: 'classico', label: 'Clássico', desc: 'Sombra forte' },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => atualizarAparencia('estiloCards', opt.value)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        (aparencia.estiloCards || 'elevado') === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Estilo do Hero (Landing Page) */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" /></svg>
                  Página Inicial
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { value: 'simples', label: 'Simples', desc: 'Logo + botões' },
                    { value: 'banner', label: 'Com Banner', desc: 'Imagem de destaque' },
                    { value: 'split', label: 'Dividido', desc: 'Logo à esquerda' },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => atualizarAparencia('estiloHero', opt.value)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        (aparencia.estiloHero || 'simples') === opt.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="pt-6 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                <div 
                  className="p-6 rounded-xl border border-gray-200 min-h-[280px] overflow-hidden"
                  style={
                    aparencia.tipoFundo === 'gradiente'
                      ? { background: `linear-gradient(${aparencia.gradienteAngulo || 135}deg, ${aparencia.gradienteCor1 || '#667eea'}, ${aparencia.gradienteCor2 || '#764ba2'})` }
                      : aparencia.tipoFundo === 'imagem' && aparencia.imagemFundo
                        ? { backgroundImage: `url(${aparencia.imagemFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                        : { backgroundColor: aparencia.corFundo || '#F3F4F6' }
                  }
                >
                  {/* Mini header preview */}
                  <div 
                    className="rounded-t-lg px-4 py-2 flex items-center gap-2 -mx-2 -mt-2 mb-4"
                    style={
                      (aparencia.estiloHeader || 'gradiente') === 'gradiente'
                        ? { background: `linear-gradient(135deg, ${aparencia.corPrimaria}, ${aparencia.corSecundaria || '#1E40AF'})` }
                        : (aparencia.estiloHeader || 'gradiente') === 'transparente'
                          ? { backgroundColor: 'rgba(0,0,0,0.15)', backdropFilter: 'blur(8px)' }
                          : { backgroundColor: aparencia.corPrimaria }
                    }
                  >
                    {aparencia.logo ? (
                      <img src={aparencia.logo} alt="" className="h-5 w-auto" />
                    ) : (
                      <div className="w-5 h-5 rounded bg-white/30 flex items-center justify-center text-white text-[10px] font-bold">
                        {(nome || 'P').charAt(0)}
                      </div>
                    )}
                    <span className="text-white text-xs font-medium">{nome || 'Portal'}</span>
                  </div>
                  
                  {/* Mini card preview */}
                  <div className={`rounded-xl overflow-hidden max-w-xs mx-auto ${
                    (aparencia.estiloCards || 'elevado') === 'glass' ? 'bg-white/70 backdrop-blur shadow-xl border border-white/30' :
                    (aparencia.estiloCards || 'elevado') === 'flat' ? 'bg-white border border-gray-200' :
                    (aparencia.estiloCards || 'elevado') === 'elevado' ? 'bg-white shadow-xl shadow-gray-200/50 border border-gray-100' :
                    'bg-white shadow-lg'
                  }`}>
                    {aparencia.imagemBanner && (
                      <img src={aparencia.imagemBanner} alt="Banner" className="w-full h-16 object-cover" />
                    )}
                    <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${aparencia.corPrimaria}, ${aparencia.corSecundaria || '#1E40AF'})` }} />
                    <div className="p-4">
                      {aparencia.logo && (
                        <img src={aparencia.logo} alt="Logo" className="h-6 mb-2" />
                      )}
                      <h4 className="font-bold text-sm" style={{ color: aparencia.corPrimaria }}>{nome || 'Nome do Portal'}</h4>
                      <p className="text-[10px] mt-1 text-gray-400">{textos.mensagemInicial}</p>
                      <div className="mt-3 space-y-1.5">
                        <button 
                          className="w-full py-1.5 rounded-lg text-white text-[11px] font-medium"
                          style={{ background: `linear-gradient(135deg, ${aparencia.corPrimaria}, ${aparencia.corSecundaria || '#1E40AF'})` }}
                        >
                          Nova Solicitação
                        </button>
                        <button
                          className="w-full py-1.5 rounded-lg text-[11px] font-medium border-2 bg-white/80"
                          style={{ borderColor: aparencia.corPrimaria + '40', color: aparencia.corPrimaria }}
                        >
                          Acompanhar
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-center text-[9px] text-gray-400 mt-3">Powered by <strong>Escalone</strong></p>
                </div>
              </div>
            </div>
          )}

          {/* Aba Funcionalidades */}
          {abaAtiva === 'funcionalidades' && (
            <div className="space-y-6">
              {/* Campo de Título */}
              <div>
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Campo de Título
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.mostrarCampoTitulo !== false}
                      onChange={(e) => atualizarFuncionalidades('mostrarCampoTitulo', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Exibir campo de título no formulário</p>
                      <p className="text-sm text-gray-500">Permite que o usuário digite um título para sua solicitação (usado como título do cartão)</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Chat e Acompanhamento */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                  Chat e Acompanhamento
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.permitirAcompanhamento}
                      onChange={(e) => atualizarFuncionalidades('permitirAcompanhamento', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Permitir acompanhamento</p>
                      <p className="text-sm text-gray-500">O usuário externo poderá acessar uma página para ver o status da solicitação</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.permitirChat}
                      onChange={(e) => atualizarFuncionalidades('permitirChat', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Permitir chat</p>
                      <p className="text-sm text-gray-500">O usuário externo poderá enviar mensagens e tirar dúvidas pela página de acompanhamento</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={!!funcionalidades.acessoPorCodigo}
                      onChange={(e) => atualizarFuncionalidades('acessoPorCodigo', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Exigir código por e-mail</p>
                      <p className="text-sm text-gray-500">Para ver as solicitações, a pessoa confirma o e-mail com um código de 6 dígitos enviado para ele. Sem isso, basta digitar um e-mail para ver as solicitações dele</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.gerarProtocolo}
                      onChange={(e) => atualizarFuncionalidades('gerarProtocolo', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Gerar protocolo</p>
                      <p className="text-sm text-gray-500">Cada submissão receberá um número de protocolo único para referência</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.exibirEtapaAtual}
                      onChange={(e) => atualizarFuncionalidades('exibirEtapaAtual', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Exibir etapa atual</p>
                      <p className="text-sm text-gray-500">O usuário verá em qual etapa do fluxo o card está</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Anexos */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4">Anexos</h4>
                <div className="space-y-4">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={funcionalidades.permitirAnexos}
                      onChange={(e) => atualizarFuncionalidades('permitirAnexos', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Permitir anexos</p>
                      <p className="text-sm text-gray-500">Usuários podem enviar arquivos junto com a submissão</p>
                    </div>
                  </label>

                  {funcionalidades.permitirAnexos && (
                    <div className="ml-8 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tamanho máximo por arquivo (MB)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={funcionalidades.limitarTamanhoAnexo || 10}
                          onChange={(e) => atualizarFuncionalidades('limitarTamanhoAnexo', parseInt(e.target.value))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Extensões permitidas
                        </label>
                        <input
                          type="text"
                          value={(funcionalidades.extensoesPermitidas || []).join(', ')}
                          onChange={(e) => atualizarFuncionalidades('extensoesPermitidas', 
                            e.target.value.split(',').map(ext => ext.trim().toLowerCase()).filter(Boolean)
                          )}
                          placeholder="pdf, jpg, png, doc, xls"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separe as extensões por vírgula</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Aba Notificações */}
          {abaAtiva === 'notificacoes' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Nota:</strong> As notificações por e-mail requerem configuração do servidor de e-mail. 
                  Contate o administrador do sistema para configurar.
                </p>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <BellIcon className="h-5 w-5" />
                  E-mails Automáticos
                </h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={notificacoes.enviarEmailConfirmacao}
                      onChange={(e) => atualizarNotificacoes('enviarEmailConfirmacao', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">E-mail de confirmação</p>
                      <p className="text-sm text-gray-500">Enviar e-mail ao usuário externo confirmando o recebimento da submissão</p>
                    </div>
                  </label>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={notificacoes.enviarEmailAtualizacao}
                      onChange={(e) => atualizarNotificacoes('enviarEmailAtualizacao', e.target.checked)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <p className="font-medium text-gray-900">E-mail de atualização</p>
                      <p className="text-sm text-gray-500">Notificar o usuário externo quando houver atualizações (mudança de etapa, mensagens)</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-4">Notificações Internas</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    E-mails para notificação (equipe interna)
                  </label>
                  <input
                    type="text"
                    value={(notificacoes.emailsInternosNotificacao || []).join(', ')}
                    onChange={(e) => atualizarNotificacoes('emailsInternosNotificacao', 
                      e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                    )}
                    placeholder="email1@empresa.com, email2@empresa.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Estes e-mails receberão notificação quando uma nova submissão for feita ou mensagem recebida
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            disabled={salvando}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {salvando && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
            {portal ? 'Salvar Alterações' : 'Criar Portal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Portais;
